import { useError } from "../../context/useErrorContext"
import { useLoad } from "../../context/useLoadContext"
import { useTranslation } from "react-i18next"
import { API_ROUTES } from "../../configuration/RoutesApi"
import { DeleteRequest, GetRequest, PostRequest } from "../../services/ApiClientService"
import type { IPrinterConfigurationDto } from "../../Objects/PrintAutomation/PrintAutomation"
import type { EPrintType } from "../../Enum/EPrintType"
import type { IPrintAgentOverviewDto } from "../../Objects/PrintAutomation/IPrintAgentOverviewDto"

type ApiWrappedResponse<T> = {
  success?: boolean
  message?: string
  data?: T
}

export type PairingCodeResult = {
  agentId: number
  pairingCode: string
  expiresAt: string
}

const normalizePrintType = (printType: unknown): EPrintType => {
  if (typeof printType === "string") {
    const mapByString: Record<string, EPrintType> = {
      Fulfillment: "Fulfillment",
      loja: "loja",
      Delivery: "Delivery",
      All: "All",
      Customer: "Delivery",
    }

    return mapByString[printType] ?? "Fulfillment"
  }

  if (typeof printType === "number") {
    const mapByNumber: Record<number, EPrintType> = {
      1: "Fulfillment",
      2: "loja",
      3: "Delivery",
      4: "All",
    }

    return mapByNumber[printType] ?? "Fulfillment"
  }

  return "Fulfillment"
}

const normalizePrinter = (printer: IPrinterConfigurationDto): IPrinterConfigurationDto => ({
  ...printer,
  printType: normalizePrintType(printer.printType),
})

const normalizeAgent = (agent: IPrintAgentOverviewDto): IPrintAgentOverviewDto => ({
  ...agent,
  printers: (agent.printers ?? []).map(normalizePrinter),
})

function hasWrappedStructure<T>(response: unknown): response is ApiWrappedResponse<T> {
  return typeof response === "object" && response !== null && "success" in response
}

export const usePrintAutomation = () => {
  const { t } = useTranslation("general")

  const { enableLoad, disableLoad } = useLoad()

  const { setError } = useError()

  const resolveErrorMessage = (message?: string) => {
    const trimmed = message?.trim()
    return trimmed && trimmed.length > 0 ? trimmed : t("label_unknown_error_description")
  }

  function unwrapResponseData<T>(response: unknown, fallbackValue: T): T {
    if (hasWrappedStructure<T>(response)) {
      if (response.success === false) {
        throw new Error(resolveErrorMessage(response.message))
      }

      return (response.data ?? fallbackValue) as T
    }

    if (response === null || response === undefined) {
      return fallbackValue
    }

    return response as T
  }

  const printTypeToNumber = (value: EPrintType): number => {
    const map: Record<EPrintType, number> = {
      Fulfillment: 1,
      loja: 2,
      Delivery: 3,
      All: 4,
    }

    return map[value]
  }

  const getOverview = async (): Promise<IPrintAgentOverviewDto[]> => {
    enableLoad()

    try {
      const response = await GetRequest<IPrintAgentOverviewDto[]>(API_ROUTES.PRINT_AUTOMATION.OVERVIEW)
      return unwrapResponseData<IPrintAgentOverviewDto[]>(response, []).map(normalizeAgent)
    } catch (e: unknown) {
      const message = e instanceof Error ? resolveErrorMessage(e.message) : t("label_unknown_error_description")
      setError(t("label_title_erro"), message)
      return []
    } finally {
      disableLoad()
    }
  }

  const generatePairingCode = async (agentName: string): Promise<PairingCodeResult> => {
    enableLoad()

    try {
      const response = await PostRequest<PairingCodeResult>(API_ROUTES.PRINT_AUTOMATION.GENERATE_PAIRING_CODE, {
        agentName,
      })

      return unwrapResponseData(response, { agentId: 0, pairingCode: "", expiresAt: "" })
    } catch (e: unknown) {
      const message = e instanceof Error ? resolveErrorMessage(e.message) : t("label_unknown_error_description")
      setError(t("label_title_erro"), message)
      throw e
    } finally {
      disableLoad()
    }
  }

  const savePrinter = async (request: IPrinterConfigurationDto) => {
    enableLoad()

    try {
      const payload = {
        ...request,
        printType: printTypeToNumber(request.printType),
      }

      const response = await PostRequest<IPrinterConfigurationDto>(API_ROUTES.PRINT_AUTOMATION.PRINTERS, payload)
      return normalizePrinter(unwrapResponseData(response, request))
    } catch (e: unknown) {
      const message = e instanceof Error ? resolveErrorMessage(e.message) : t("label_unknown_error_description")
      setError(t("label_title_erro"), message)
      throw e
    } finally {
      disableLoad()
    }
  }

  const testPrint = async (printerConfigurationId: number) => {
    enableLoad()

    try {
      const response = await PostRequest<object>(API_ROUTES.PRINT_AUTOMATION.TEST_PRINT(printerConfigurationId), {})
      unwrapResponseData(response, {})
    } catch (e: unknown) {
      const message = e instanceof Error ? resolveErrorMessage(e.message) : t("label_unknown_error_description")
      setError(t("label_title_erro"), message)
      throw e
    } finally {
      disableLoad()
    }
  }

  const deleteAgent = async (agentId: number) => {
    enableLoad()

    try {
      const response = await DeleteRequest<object>(API_ROUTES.PRINT_AUTOMATION.DELETE_AGENT(agentId))
      unwrapResponseData(response, {})
    } catch (e: unknown) {
      const message = e instanceof Error ? resolveErrorMessage(e.message) : t("label_unknown_error_description")
      setError(t("label_title_erro"), message)
      throw e
    } finally {
      disableLoad()
    }
  }

  return { getOverview, generatePairingCode, savePrinter, testPrint, deleteAgent }
}
